import { IAlphaMessage } from 'store/alpha/types';

const keyOf = (message: IAlphaMessage): string => `${message.role}\n${message.text}`;

const countKeys = (messages: IAlphaMessage[], counts: Map<string, number>, step: number): void => {
  messages.forEach(message => {
    const key = keyOf(message);
    counts.set(key, (counts.get(key) ?? 0) + step);
  });
};

export const isLocalOnly = (message: IAlphaMessage): boolean =>
  message.serverId === undefined && message.localId === undefined;

type Anchor = number | 'start';
type Placement = Anchor | 'wait' | 'drop';

export const noticeAnchor = (
  messages: IAlphaMessage[],
): Pick<IAlphaMessage, 'anchor' | 'anchorOffset'> => {
  const previous = messages[messages.length - 1];
  if (previous?.localNotice) {
    return { anchor: previous.anchor, anchorOffset: previous.anchorOffset };
  }
  let offset = 0;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.serverId !== undefined) {
      return offset === 0
        ? { anchor: message.serverId }
        : { anchor: message.serverId, anchorOffset: offset };
    }
    if (!message.localNotice) offset += 1;
  }
  return offset === 0 ? { anchor: 'start' } : { anchor: 'start', anchorOffset: offset };
};

const placementOf = (notice: IAlphaMessage, stored: IAlphaMessage[]): Placement => {
  const anchor = notice.anchor ?? stored[stored.length - 1]?.serverId ?? 'start';
  const offset = notice.anchorOffset ?? 0;
  if (offset === 0) return anchor;
  const from = anchor === 'start' ? -1 : stored.findIndex(message => message.serverId === anchor);
  if (anchor !== 'start' && from < 0) return 'drop';
  return stored[from + offset]?.serverId ?? 'wait';
};

const placeNotices = (
  stored: IAlphaMessage[],
  notices: Array<[IAlphaMessage, Anchor]>,
): IAlphaMessage[] => {
  const after = new Map<Anchor, IAlphaMessage[]>();
  notices.forEach(([notice, anchor]) => {
    const pinned =
      notice.anchor === anchor && notice.anchorOffset === undefined
        ? notice
        : { ...notice, anchor, anchorOffset: undefined };
    after.set(anchor, [...(after.get(anchor) ?? []), pinned]);
  });
  return [
    ...(after.get('start') ?? []),
    ...stored.flatMap(message => [
      message,
      ...(message.serverId === undefined ? [] : after.get(message.serverId) ?? []),
    ]),
  ];
};

export const mergeReload = (
  stored: IAlphaMessage[],
  seenAtStart: IAlphaMessage[],
  current: IAlphaMessage[],
): IAlphaMessage[] => {
  const newestKnown = seenAtStart.reduce(
    (newest, message) => Math.max(newest, message.serverId ?? -Infinity),
    -Infinity,
  );
  const storedSinceStart = new Map<string, number>();
  countKeys(
    stored.filter(message => (message.serverId ?? Infinity) > newestKnown),
    storedSinceStart,
    1,
  );
  countKeys(
    seenAtStart.filter(message => message.serverId === undefined && message.localId !== undefined),
    storedSinceStart,
    -1,
  );
  const placements = new Map(
    current
      .filter(message => message.localNotice)
      .map((notice): [IAlphaMessage, Placement] => [notice, placementOf(notice, stored)]),
  );
  const kept = current.filter(isLocalOnly).filter(message => {
    if (message.localNotice) return placements.get(message) === 'wait';
    const key = keyOf(message);
    const copies = storedSinceStart.get(key) ?? 0;
    if (copies <= 0) return true;
    storedSinceStart.set(key, copies - 1);
    return false;
  });
  const placed = [...placements].filter(
    (entry): entry is [IAlphaMessage, Anchor] => entry[1] !== 'wait' && entry[1] !== 'drop',
  );
  return [...placeNotices(stored, placed), ...kept];
};
